import {
  forwardRef,
  useContext,
  useRef,
  useEffect,
  useMemo,
  useState,
  useLayoutEffect,
  useCallback
} from 'react';
import type { PropsWithoutRef, Ref, CSSProperties, ReactElement, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import type { PopperProps } from 'react-popper';
import type { VirtualElement } from '@popperjs/core';
import type { OffsetModifier } from '@popperjs/core/lib/modifiers/offset';

import type { BaseProps, ForwardProps } from '../../types';
import {
  useAfterInitialEffect,
  useConfiguration,
  useConsolidatedRef,
  useElement,
  useEvent,
  usePopoverMap,
  useUID
} from '../../hooks';
import { getRelativeOffset, isInstance, windowIsAvailable } from '../../utils';
import type { ConfigurationProps } from '../Configuration';

import { PopoverManagerContext } from './PopoverContext';
import { StyledPopover, StyledPopoverArrow } from './Popover.styles';
import { placeAndContain } from './modifiers';

export type Delay = 'none' | 'short' | 'long';
export interface PopoverProps<Modifiers extends string = string> extends BaseProps {
  /** The DOM element or [virtual element](https://popper.js.org/docs/v2/virtual-elements) that the Popover is positioned in reference to. */
  target: Element | VirtualElement | null;
  /**
   * If true, the Popover will be visible.
   * @default true
   */
  show?: boolean;
  /**
   * If true, the Popover will render outside of the current DOM hierarchy in a [portal](https://reactjs.org/docs/portals.html).
   * @default true
   */
  portal?: boolean | ConfigurationProps['portalTarget'];
  /**
   * Delay on showing the Popover.
   * @default 'none'
   */
  showDelay?: Delay;
  /**
   * Delay on hiding the Popover.
   * @default 'none'
   */
  hideDelay?: Delay;
  /**
   * [React Popper Placement](https://popper.js.org/react-popper/v2/render-props/#placement)
   * @default 'bottom'
   */
  placement?: PopperProps<Modifiers>['placement'];
  /**
   * [React Popper Strategy](https://popper.js.org/react-popper/v2/render-props/#strategy)
   * @default 'absolute'
   */
  strategy?: PopperProps<Modifiers>['strategy'];
  /**
   * [React Popper Modifiers](https://popper.js.org/react-popper/v2/render-props/#modifiers)
   * @default []
   */
  modifiers?: PopperProps<Modifiers>['modifiers'];
  /**
   * If true, an arrow will visually identify from which element the Popover is rendered.
   * @default false
   */
  arrow?: boolean;
  /** The group that the Popover belongs to. Popovers in the same group will be closed when others open. */
  groupId?: string;
  /**
   * If true, the Popover will be visibly hidden when its target leaves the viewport.
   * @default false
   */
  hideOnTargetHidden?: boolean;
  /** Callback that runs when the popover is hidden */
  onHide?: () => void;
  /** Callback that runs when the popover is shown */
  onShow?: () => void;
  /** The content of the Popover. */
  children: ReactNode;
  /** The ref of the wrapping element. */
  ref?: Ref<HTMLDivElement>;
}

const delays = {
  none: 0,
  short: 500,
  long: 1000
};

const Popover = forwardRef(function Popover(
  {
    show = true,
    portal = true,
    target,
    placement = 'bottom',
    strategy = 'fixed',
    style,
    modifiers = [],
    arrow = false,
    groupId,
    showDelay = 'none',
    hideDelay = 'none',
    hideOnTargetHidden = false,
    children,
    onHide,
    onShow,
    ...restProps
  }: PropsWithoutRef<PopoverProps> & { style?: CSSProperties },
  ref: PopoverProps['ref']
) {
  const { portalTarget: configurationPortal } = useConfiguration();
  const { checkActive, setActive, popovers } = useContext(PopoverManagerContext);
  const [popperEl, setPopperEl] = useElement<HTMLDivElement>();
  const popperRef = useConsolidatedRef<HTMLDivElement>(ref, setPopperEl);
  const arrowRef = useRef<HTMLDivElement>(null);
  const forceUpdateRef = useRef<() => void>();
  const initialOffset = useRef<Pick<DOMRect, 'x' | 'y'> | null>(null);
  const [showPopover, setShowPopover] = useState(show);
  const timeout = useRef<number>();
  const uid = useUID();

  const resolvedTarget = isInstance(target, Element) ? target : target?.contextElement;

  const portalTarget = typeof portal === 'boolean' ? configurationPortal : portal;

  const memoedModifiers = useMemo(() => {
    const flipEnabled = !!modifiers
      .slice()
      .reverse()
      .find(modifier => modifier.name === 'flip' && modifier.enabled !== undefined)?.enabled;

    return [
      {
        name: 'computeStyles',
        options: {
          gpuAcceleration: false
        }
      },
      {
        name: 'offset',
        options: {
          offset: [0, arrow ? 5.5 : 2]
        }
      },
      { name: 'hide', enabled: hideOnTargetHidden },
      { name: 'arrow', enabled: arrow, options: { element: arrowRef.current, padding: 7 } },
      {
        name: 'preventOverflow',
        options: {
          tether: !modifiers.find(m => m.name === 'sameWidth' || 'fitToContent'),
          padding: 16
        }
      },
      {
        ...placeAndContain,
        enabled: !flipEnabled
      },
      ...modifiers
    ];
  }, [arrow, hideOnTargetHidden, modifiers]);

  const offset = useMemo(() => {
    const offsetModifier = [...memoedModifiers]
      .reverse()
      .find(m => m.name === 'offset' && m.enabled !== false) as OffsetModifier | undefined;
    return Array.isArray(offsetModifier?.options?.offset)
      ? offsetModifier?.options?.offset[1] ?? 0
      : 0;
  }, [memoedModifiers]);

  const { styles, attributes, forceUpdate } = usePopper(target, popperEl, {
    placement,
    strategy,
    modifiers: memoedModifiers
  });

  if (forceUpdate) forceUpdateRef.current = forceUpdate;

  // Setter-less useState to avoid re-running these constructors on every render with useRef.
  const [ac] = useState(() => new AbortController());
  const [observedSet] = useState(() => new WeakSet<Element>());
  const [ro] = useState(
    () =>
      new ResizeObserver(entries => {
        let updateQueued = false;

        for (const { target: resizeTarget } of entries) {
          // Ignore the first ResizeObserver event for each element since ResizeObserver always fires once when calling .observe().
          if (!observedSet.has(resizeTarget)) {
            observedSet.add(resizeTarget);
          } else if (!updateQueued) {
            updateQueued = true;

            if (!ac.signal.aborted) {
              forceUpdateRef.current?.();
            }
          }
        }
      })
  );

  useEffect(() => {
    return () => {
      ac.abort();
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!popperEl) return;

    ro.observe(popperEl);

    return () => {
      ro.unobserve(popperEl);
    };
  }, [popperEl]);

  useEffect(() => {
    if (!resolvedTarget) return;

    ro.observe(resolvedTarget);

    return () => {
      ro.unobserve(resolvedTarget);
    };
  }, [resolvedTarget]);

  useLayoutEffect(() => {
    if (resolvedTarget) {
      initialOffset.current = getRelativeOffset(resolvedTarget, document.documentElement);
    } else {
      initialOffset.current = null;
    }
  }, [resolvedTarget]);

  useEffect(() => {
    if (!showPopover || !resolvedTarget) return;

    const rafAC = new AbortController();
    let rafID: ReturnType<typeof requestAnimationFrame>;

    rafAC.signal.addEventListener('aborted', () => {
      cancelAnimationFrame(rafID);
    });

    const checkCoordsAndConnected = () => {
      if (!resolvedTarget.isConnected) {
        setShowPopover(false);
        return;
      }

      if (initialOffset.current) {
        const newOffset = getRelativeOffset(resolvedTarget, document.documentElement);

        if (initialOffset.current.x !== newOffset.x || initialOffset.current.y !== newOffset.y) {
          initialOffset.current = newOffset;
          forceUpdateRef.current?.();
        }
      }

      if (!rafAC.signal.aborted) {
        rafID = requestAnimationFrame(checkCoordsAndConnected);
      }
    };

    rafID = requestAnimationFrame(checkCoordsAndConnected);

    return () => {
      rafAC.abort();
    };
  }, [showPopover, resolvedTarget]);

  useEffect(() => {
    if (groupId && show) setActive(uid, groupId);

    /**
     *  TODO: Remove usages of forceUpdateRef.current?.() below in favor of permanent fix to address following issues:
     *  * Resize observer is not running immediately after initial mount
     *  * Menu scrollAt is not applied on initial render
     */
    if (windowIsAvailable) {
      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = window.setTimeout(
        () => {
          setShowPopover(show);
          if (show) forceUpdateRef.current?.();
        },
        delays[show ? showDelay : hideDelay]
      );
    } else {
      setShowPopover(show);
      if (show) forceUpdateRef.current?.();
    }
  }, [show]);

  useEffect(() => {
    return () => clearTimeout(timeout.current);
  }, []);

  useAfterInitialEffect(() => {
    if (!showPopover) {
      onHide?.();
    } else {
      onShow?.();
    }
  }, [showPopover]);

  useEffect(() => {
    if (groupId && popovers[groupId] && popovers[groupId] !== uid) {
      setShowPopover(false);
    }
  }, [groupId ? popovers[groupId] : undefined]);

  const popoverId = usePopoverMap(resolvedTarget, !!portal);

  const triggerPopoverFocusEvent = useCallback(
    (e: Event) => {
      if (resolvedTarget && portal) {
        const event = new Event(e.type.startsWith('popover:') ? e.type : `popover:${e.type}`, {
          bubbles: true,
          cancelable: true
        });
        resolvedTarget.dispatchEvent(event);
      }
    },
    [resolvedTarget, portal]
  );

  // need to retrigger focus events on the target element to correctly notify useFocusWithin hook if popover is portal-ed
  useEvent('focusout', triggerPopoverFocusEvent, { target: popperRef });
  useEvent('popover:focusout', triggerPopoverFocusEvent, { target: popperRef });
  useEvent('focusin', triggerPopoverFocusEvent, { target: popperRef });
  useEvent('popover:focusin', triggerPopoverFocusEvent, { target: popperRef });

  const content = (
    <StyledPopover
      {...restProps}
      data-popover-id={popoverId}
      portal={!!(portal && portalTarget)}
      offset={offset}
      ref={popperRef}
      style={{ ...style, ...styles.popper }}
      {...attributes.popper}
    >
      {arrow && <StyledPopoverArrow ref={arrowRef} style={{ ...styles.arrow }} />}
      {children}
    </StyledPopover>
  );

  if (!target || !showPopover || (groupId && !checkActive(uid, groupId))) return null;

  return portal && portalTarget ? createPortal(content, portalTarget) : content;
}) as <Modifiers extends string = string>(
  props: PopoverProps<Modifiers> & ForwardProps
) => ReactElement | null;

export default Popover;
